
import { useEffect } from 'react';
import { useIsMobile } from './use-mobile';

export const useMobileAccessibility = () => {
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isMobile) return;

    // Fonction pour s'assurer des tailles de tap target minimales
    const ensureMinTapSize = () => {
      const interactiveElements = document.querySelectorAll(
        'button:not([data-small-ok]):not(.admin-button), a:not([data-small-ok]):not(.admin-link), input, select, textarea, [role="button"]:not([data-small-ok])'
      );

      interactiveElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        
        // Exclure les éléments admin/PDF
        if (htmlElement.closest('[data-admin-preview]') || 
            htmlElement.closest('.invoice-preview') ||
            htmlElement.closest('.admin-panel')) {
          return;
        }
        
        const computedStyle = window.getComputedStyle(htmlElement);
        const minSize = 44; // 44px minimum tap target (WCAG recommandation)

        // Get current dimensions
        const width = parseInt(computedStyle.width);
        const height = parseInt(computedStyle.height);

        // Only apply if element is too small and not inline
        if ((width < minSize || height < minSize) && computedStyle.display !== 'inline') {
          if (height < minSize) {
            htmlElement.style.minHeight = `${minSize}px`;
          }
          if (width < minSize) {
            htmlElement.style.minWidth = `${minSize}px`;
          }
        }
      });
    };

    // Fonction pour s'assurer des tailles de police minimales (12px)
    const ensureMinFontSize = () => {
      const textElements = document.querySelectorAll('p, span:not(.sr-only), label, div');
      
      textElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        
        // Exclure les éléments admin/PDF
        if (htmlElement.closest('[data-admin-preview]') || 
            htmlElement.closest('.invoice-preview') ||
            htmlElement.closest('.admin-panel') ||
            htmlElement.classList.contains('admin-text') ||
            htmlElement.classList.contains('pdf-text')) {
          return;
        }
        
        const computedStyle = window.getComputedStyle(htmlElement);
        const fontSize = parseFloat(computedStyle.fontSize);
        
        // Appliquer 12px minimum seulement si la police est trop petite
        if (fontSize < 12 && fontSize > 0) {
          htmlElement.style.fontSize = '12px';
        }
      });
    };

    // Les scans (querySelectorAll plein-document + getComputedStyle en boucle)
    // sont coûteux et forcent des reflows. On les défère en requestIdleCallback
    // pour qu'ils ne rivalisent JAMAIS avec le paint/scroll (cause du jank au
    // scroll quand les sections lazy se révélaient). La couverture est conservée
    // (les éléments ajoutés dynamiquement sont toujours traités, juste au prochain
    // temps d'inactivité).
    const ric: (cb: () => void) => void =
      (window as any).requestIdleCallback
        ? (cb) => (window as any).requestIdleCallback(cb, { timeout: 500 })
        : (cb) => requestAnimationFrame(cb);

    const runOptimizations = () => {
      ric(() => {
        ensureMinTapSize();
        ensureMinFontSize();
      });
    };

    // Run on mount
    runOptimizations();

    // Observer les changements du DOM (debounce élargi à 250ms : la cascade de
    // sections lazy génère beaucoup de mutations rapprochées au 1er scroll).
    const observer = new MutationObserver(() => {
      clearTimeout((window as any).__accessibilityTimeout);
      (window as any).__accessibilityTimeout = setTimeout(runOptimizations, 250);
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
      clearTimeout((window as any).__accessibilityTimeout);
    };
  }, [isMobile]);

  return { isMobile };
};
